import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { apiRequest } from "../../utils/apiClient";
import { useVendorContext } from "../../context/VendorContext";
import { Order } from "../../types/Interface";

const COLORS = ["#d4af37", "#4f46e5", "#0ea5e9", "#22c55e", "#f97316", "#ef4444"];

const Spinner = () => (
  <div className="flex justify-center items-center h-40">
    <svg className="animate-spin h-6 w-6 text-gray-400" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
    </svg>
  </div>
);

const OrdersKPIs = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedFilter, setSelectedFilter] = useState<{
    type: "category" | "status" | "vendor" | "vendor-category";
    value: string;
  } | null>(null);

  const { vendor } = useVendorContext();
  const isCompany = vendor?.role === "COMPANY";
  const vendorName = vendor?.vendorName?.toLowerCase();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await apiRequest("/orders");
        setOrders(response);
      } catch (error) {
        console.error("Error fetching orders:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredOrders = orders.filter((order) =>
    isCompany ? true : order.orderItems.some((item) => item.product?.vendor?.toLowerCase() === vendorName)
  );

  const totalOrders = filteredOrders.length;
  const totalRevenue = filteredOrders.reduce((sum, order) => sum + order.orderTotal, 0);
  const avgOrderValue = totalOrders > 0 ? Math.round(totalRevenue / totalOrders) : 0;
  const activeOrders = filteredOrders.filter((order) =>
    ["PENDING", "PLACED", "PROCESSING", "SHIPPED", "OUT_FOR_DELIVERY", "CONFIRMED"].includes((order.status || "").toUpperCase())
  ).length;

  const now = new Date();
  const currentWindowStart = new Date(now);
  currentWindowStart.setDate(now.getDate() - 7);
  const previousWindowStart = new Date(now);
  previousWindowStart.setDate(now.getDate() - 14);

  const parseDate = (value?: string) => {
    if (!value) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const currentWindowOrders = filteredOrders.filter((order) => {
    const orderDate = parseDate(order.createdAt);
    return !!orderDate && orderDate >= currentWindowStart && orderDate <= now;
  });

  const previousWindowOrders = filteredOrders.filter((order) => {
    const orderDate = parseDate(order.createdAt);
    return !!orderDate && orderDate >= previousWindowStart && orderDate < currentWindowStart;
  });

  const currentOrdersCount = currentWindowOrders.length;
  const previousOrdersCount = previousWindowOrders.length;
  const currentRevenue = currentWindowOrders.reduce((sum, order) => sum + order.orderTotal, 0);
  const previousRevenue = previousWindowOrders.reduce((sum, order) => sum + order.orderTotal, 0);
  const currentAov = currentOrdersCount > 0 ? Math.round(currentRevenue / currentOrdersCount) : 0;
  const previousAov = previousOrdersCount > 0 ? Math.round(previousRevenue / previousOrdersCount) : 0;
  const currentActive = currentWindowOrders.filter((order) =>
    ["PENDING", "PLACED", "PROCESSING", "SHIPPED", "OUT_FOR_DELIVERY", "CONFIRMED"].includes((order.status || "").toUpperCase())
  ).length;
  const previousActive = previousWindowOrders.filter((order) =>
    ["PENDING", "PLACED", "PROCESSING", "SHIPPED", "OUT_FOR_DELIVERY", "CONFIRMED"].includes((order.status || "").toUpperCase())
  ).length;

  const categoryMap: Record<string, number> = {};
  filteredOrders.forEach((order) => {
    const category = order.orderItems[0]?.product?.category || "Unknown";
    categoryMap[category] = (categoryMap[category] || 0) + 1;
  });
  const categoryData = Object.entries(categoryMap).map(([name, value]) => ({ name, value }));

  const statusMap: Record<string, number> = {};
  filteredOrders.forEach((order) => {
    statusMap[order.status] = (statusMap[order.status] || 0) + 1;
  });
  const statusData = Object.entries(statusMap).map(([name, value]) => ({ name, value }));

  const chartFilteredOrders = selectedFilter
    ? filteredOrders.filter((order) => {
        if (selectedFilter.type === "category") {
          const category = order.orderItems[0]?.product?.category || "Unknown";
          return category === selectedFilter.value;
        }
        if (selectedFilter.type === "status") {
          return order.status === selectedFilter.value;
        }
        if (selectedFilter.type === "vendor") {
          return order.orderItems.some((item) => item.product?.vendor === selectedFilter.value);
        }
        if (selectedFilter.type === "vendor-category") {
          const [vendorFilter, categoryFilter] = selectedFilter.value.split("::");
          return order.orderItems.some(
            (item) => item.product?.vendor === vendorFilter && item.product?.category === categoryFilter
          );
        }
        return true;
      })
    : filteredOrders;

  const vendorOrderMap: Record<string, { orders: number; revenue: number }> = {};
  orders.forEach((order) => {
    order.orderItems.forEach((item) => {
      const vendorItem = item.product?.vendor || "Unknown";
      if (!vendorOrderMap[vendorItem]) vendorOrderMap[vendorItem] = { orders: 0, revenue: 0 };
      vendorOrderMap[vendorItem].orders += 1;
      vendorOrderMap[vendorItem].revenue += order.orderTotal;
    });
  });
  const vendorOrderData = Object.entries(vendorOrderMap).map(([name, value]) => ({
    name,
    orders: value.orders,
    revenue: value.revenue,
  }));

  const vendorCategoryMap: Record<string, Record<string, { orders: number; revenue: number }>> = {};
  orders.forEach((order) => {
    order.orderItems.forEach((item) => {
      const vendorItem = item.product?.vendor || "Unknown";
      const category = item.product?.category || "Unknown";
      if (!vendorCategoryMap[vendorItem]) vendorCategoryMap[vendorItem] = {};
      if (!vendorCategoryMap[vendorItem][category]) vendorCategoryMap[vendorItem][category] = { orders: 0, revenue: 0 };
      vendorCategoryMap[vendorItem][category].orders += 1;
      vendorCategoryMap[vendorItem][category].revenue += order.orderTotal;
    });
  });

  const formatCurrency = (value: number) => `₹${value.toLocaleString("en-IN")}`;

  const getTrendMeta = (current: number, previous: number) => {
    if (previous === 0 && current === 0) {
      return { text: "0% vs previous 7d", tone: "text-slate-500" };
    }
    if (previous === 0 && current > 0) {
      return { text: "+100% vs previous 7d", tone: "text-emerald-600" };
    }

    const percent = ((current - previous) / previous) * 100;
    const rounded = Math.round(percent * 10) / 10;
    const text = `${rounded > 0 ? "+" : ""}${rounded}% vs previous 7d`;
    if (rounded > 0) return { text, tone: "text-emerald-600" };
    if (rounded < 0) return { text, tone: "text-rose-600" };
    return { text, tone: "text-slate-500" };
  };

  const ordersTrend = getTrendMeta(currentOrdersCount, previousOrdersCount);
  const revenueTrend = getTrendMeta(currentRevenue, previousRevenue);
  const aovTrend = getTrendMeta(currentAov, previousAov);
  const activeTrend = getTrendMeta(currentActive, previousActive);

  const kpiCards = [
    {
      label: "Total Orders",
      value: totalOrders.toLocaleString("en-IN"),
      caption: "Across current dashboard scope",
      accent: "from-amber-50 to-white",
      trend: ordersTrend,
    },
    {
      label: "Total Revenue",
      value: formatCurrency(totalRevenue),
      caption: "Aggregated order total",
      accent: "from-blue-50 to-white",
      trend: revenueTrend,
    },
    {
      label: "Average Order Value",
      value: formatCurrency(avgOrderValue),
      caption: "Revenue per order",
      accent: "from-emerald-50 to-white",
      trend: aovTrend,
    },
    {
      label: "Active Orders",
      value: activeOrders.toLocaleString("en-IN"),
      caption: "Pending through delivery pipeline",
      accent: "from-orange-50 to-white",
      trend: activeTrend,
    },
  ];

  return (
    <div className="max-w-7xl mx-auto mt-8 px-4 pb-8 space-y-6">
      <div className="rounded-xl border border-slate-200 bg-gradient-to-r from-amber-50 via-white to-slate-50 p-4 md:p-5 shadow-sm">
        <h1 className="text-2xl font-bold text-slate-900">Landing Metrics</h1>
        <p className="text-sm text-slate-600 mt-1">
          Quick performance view of orders, revenue, and status distribution.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {kpiCards.map((card) => (
          <div
            key={card.label}
            className={`rounded-xl border border-slate-200 bg-gradient-to-br ${card.accent} p-4 shadow-sm`}
          >
            <p className="text-xs font-medium text-slate-500">{card.label}</p>
            {loading ? <Spinner /> : <p className="mt-2 text-2xl font-semibold text-slate-900">{card.value}</p>}
            <p className={`mt-1 text-xs font-semibold ${card.trend.tone}`}>{card.trend.text}</p>
            <p className="mt-1 text-xs text-slate-500">{card.caption}</p>
          </div>
        ))}
      </div>

      {isCompany && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900 mb-3">Orders and Revenue by Vendor</h2>
          {loading ? (
            <Spinner />
          ) : (
            <div className="h-[320px] w-full">
              <ResponsiveContainer>
                <BarChart data={vendorOrderData}>
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip formatter={(value: number, name: string) => (name === "Revenue" ? formatCurrency(value) : value)} />
                  <Legend />
                  <Bar
                    dataKey="orders"
                    fill="#4f46e5"
                    name="Orders"
                    onClick={(data: { name: string }) => setSelectedFilter({ type: "vendor", value: data.name })}
                  />
                  <Bar
                    dataKey="revenue"
                    fill="#d4af37"
                    name="Revenue"
                    onClick={(data: { name: string }) => setSelectedFilter({ type: "vendor", value: data.name })}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {isCompany && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900 mb-3">Vendor to Category Matrix</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left">
                  <th className="p-2 border-b border-slate-200">Vendor</th>
                  <th className="p-2 border-b border-slate-200">Category</th>
                  <th className="p-2 border-b border-slate-200">Orders</th>
                  <th className="p-2 border-b border-slate-200">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(vendorCategoryMap).flatMap(([vendorItem, categories]) =>
                  Object.entries(categories).map(([category, data], idx) => (
                    <tr
                      key={`${vendorItem}-${category}`}
                      className="border-t border-slate-100 cursor-pointer hover:bg-slate-50"
                      onClick={() => setSelectedFilter({ type: "vendor-category", value: `${vendorItem}::${category}` })}
                    >
                      <td className="p-2">{idx === 0 ? vendorItem : ""}</td>
                      <td className="p-2">{category}</td>
                      <td className="p-2">{data.orders}</td>
                      <td className="p-2">{formatCurrency(data.revenue)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900 mb-2">Orders by Category</h2>
          {loading ? (
            <Spinner />
          ) : categoryData.length > 0 ? (
            <div className="h-[300px] w-full">
              <ResponsiveContainer>
                <BarChart data={categoryData}>
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar
                    dataKey="value"
                    fill="#4f46e5"
                    onClick={(data: { name: string }) => setSelectedFilter({ type: "category", value: data.name })}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-slate-500 text-center mt-8">No category data available.</p>
          )}
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900 mb-2">Orders by Status</h2>
          {loading ? (
            <Spinner />
          ) : statusData.length > 0 ? (
            <div className="h-[300px] w-full">
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={statusData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    label
                    onClick={(_, index: number) => setSelectedFilter({ type: "status", value: statusData[index].name })}
                  >
                    {statusData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Legend />
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-slate-500 text-center mt-8">No status data available.</p>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-semibold text-slate-900">Orders Data Table</h2>
          {selectedFilter && (
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800">
                Filter: {selectedFilter.type} = {selectedFilter.value.replace("::", " → ")}
              </span>
              <button onClick={() => setSelectedFilter(null)} className="text-sm font-medium text-blue-700 hover:underline">
                Clear
              </button>
            </div>
          )}
        </div>

        <div className="overflow-x-auto">
          {loading ? (
            <Spinner />
          ) : (
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left">
                  <th className="p-2 border-b border-slate-200">Order ID</th>
                  <th className="p-2 border-b border-slate-200">User</th>
                  <th className="p-2 border-b border-slate-200">Status</th>
                  <th className="p-2 border-b border-slate-200">Total</th>
                  <th className="p-2 border-b border-slate-200">Date</th>
                </tr>
              </thead>
              <tbody>
                {chartFilteredOrders.map((order) => (
                  <tr key={order.id} className="border-t border-slate-100 hover:bg-slate-50/70">
                    <td className="p-2 font-medium text-slate-800">#{order.id}</td>
                    <td className="p-2 text-slate-700">
                      {[order.user?.name, order.user?.email, order.user?.mobile].filter(Boolean).join(" | ") || "N/A"}
                    </td>
                    <td className="p-2">
                      <span className="inline-flex rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700">
                        {order.status}
                      </span>
                    </td>
                    <td className="p-2 font-medium text-slate-800">{formatCurrency(order.orderTotal)}</td>
                    <td className="p-2 text-slate-600">{new Date(order.createdAt).toLocaleDateString("en-IN")}</td>
                  </tr>
                ))}
                {chartFilteredOrders.length === 0 && (
                  <tr>
                    <td colSpan={5} className="p-4 text-center text-slate-500">
                      No orders found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrdersKPIs;
